import { useEffect, useRef } from 'react';
import '@/styles/transitions/LiftTransition.css';

/**
 * What is behind the floors, and the marker that says which one you are on.
 *
 * The travelling itself belongs to the page — see LiftTransition.css — because
 * the page is the car. This component only draws the two things that have to
 * outlive the route swap happening in the middle of the ride: the shaft, and
 * the floor indicator riding along the navigation bar.
 */

/* Full ride once, express afterwards. A minute of lift over a session is
   charming; ten is a toll booth. */
export const FULL = { depart: 460, hold: 360, arrive: 720 };
export const EXPRESS = { depart: 300, hold: 80, arrive: 440 };

export const liftTotal = (t) => t.depart + t.hold + t.arrive;

/* Where the marker starts and ends, read off the real bar.
 *
 * Centres, not edges: the marker is one fixed size and only travels, so the
 * two label boxes contribute a distance and nothing else. Both are measured
 * from the label rather than the link, which is the same box the resting
 * marker centres on — measuring one and drawing the other is what made the
 * bar jump wider the instant it landed.
 *
 * Returns null when the links are not on screen — the phone collapses them
 * into a drawer — and the ride simply runs without a marker rather than with
 * a guessed one. */
export const measureNavFloors = () => {
  if (typeof document === 'undefined') return null;
  const label = (sel) => document.querySelector(`${sel} .nav-link-label`);
  const from = label('[data-testid="nav-home"]');
  const to = label('.nav-products-trigger');
  if (!from || !to) return null;

  const a = from.getBoundingClientRect();
  const b = to.getBoundingClientRect();
  if (!a.width || !b.width) return null;

  return {
    x0: Math.round(a.left + a.width / 2),
    x1: Math.round(b.left + b.width / 2),
    /* The resting marker sits 7px under the label and is 2px tall, so its own
       top edge is 5px down. Matching it exactly means the travelling one can
       hand over without a step. */
    y: Math.round(Math.max(a.bottom, b.bottom) + 5)
  };
};

/**
 * A still photograph of the navigation bar, mounted outside everything that
 * moves.
 *
 * The real bar is inside the page, and the page is clipped to one screen for
 * the ride — so the real bar is cut off no matter where it is put. This copy
 * is taken before the first frame moves and thrown away when the car levels.
 * It is scenery: aria-hidden, no pointer events, and the page beneath it is
 * inert for the same second and a half, so there is nothing to click at.
 */
const NavGhost = () => {
  const host = useRef(null);

  useEffect(() => {
    const real = document.querySelector('.page-transition-wrapper .premium-navigation');
    const mount = host.current;
    if (!real || !mount) return undefined;

    const ghost = real.cloneNode(true);
    ghost.setAttribute('aria-hidden', 'true');
    ghost.removeAttribute('data-testid');
    ghost.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    mount.appendChild(ghost);

    return () => ghost.remove();
  }, []);

  return <div ref={host} className="lift-nav-ghost-host" aria-hidden="true" />;
};

const LiftTransition = ({ active, timings, floors }) => {
  if (!active) return null;

  const total = liftTotal(timings);

  return (
    <>
      <div
        className="lift-shaft"
        aria-hidden="true"
        style={{ '--lift-total': `${total}ms` }}
      >
        <div className="lift-shaft__slabs" />
        {/* Signage on the shaft wall, drifting down because everything
            outside a rising car does.
            It was a word before, and a word needs time this gap does not
            have — it went past unread and looked like something left behind.
            A mark is recognised rather than read, so the monogram leads and
            the name sits under it as a caption. */}
        <div className="lift-shaft__mark">
          <img
            className="lift-shaft__mark-logo"
            src="/img/logo-alt-160.webp"
            alt=""
            width="160"
            height="160"
            decoding="async"
            draggable="false"
          />
          <span className="lift-shaft__mark-text">RETRO STORE</span>
          <span className="lift-shaft__mark-sweep" />
        </div>
      </div>
      <NavGhost />
      {floors && (
        <div
          className="lift-marker is-travelling"
          aria-hidden="true"
          style={{
            '--mk-x0': `${floors.x0}px`,
            '--mk-dx': `${floors.x1 - floors.x0}px`,
            '--mk-y': `${floors.y}px`,
            '--lift-total': `${total}ms`
          }}
        />
      )}
    </>
  );
};

export default LiftTransition;
